import { WizConstants } from '../../model/constants';
import { AkmFDPairs, AkmFDVCell, AkmPack, IBaseline, IBaselineValue, SisterResult } from '../../model/interfaces';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { WidgetCell, WidgetLine } from '../../vo/workingdata/widget-line.obj';
import { CellMap } from './cell.map';


/** la matrice de cellules et de valeurs de FDV */
export class CellMatrixEngine {

    // constructeur
    // avec la possibilité de lui passer une matrice. Par défaut, on utilise une cellMap
    constructor(matrix: CellMap = null) {
        this.matrix = matrix || new CellMap();
    }

    // La matrice de cellules
    private matrix: CellMap;

    public getMatrix(): CellMap {
        return this.matrix;
    }

    // Les cellules non calculées lors de la construction
    // map sous la forme: 
    // level requis pour le calcul 
    // => scenario
    // => level cible
    //  => bl.key
    //      => mKey de la cellule à rejouer = IBaselineValue
    // le step requis, une fois calculé, va appeler tous les level+scen pour jouer toutes leurs dépendances
    //                       level requis  scenario  level cible    blKey    code mesure                    
    public pendingCellMap: Map<number, Map<string, Map<number, Map<string, Map<string, WidgetCell>>>>> = new Map();

    // map sous la forme: codeFD = codeFDV = AkmFDVCell
    private fdvCellMap: Map<string, Map<string, AkmFDVCell>> = new Map();

    // map de dépendance directement sur les FD (fonction THRESHOLD)
    // code FD = AkmFDPairs
    private fdCellMap: Map<string, AkmFDPairs> = new Map();

    /**
     * 
     * @returns retourne la matrice du référentiel Freedata
     * utilisé uniquement pour compute
     */
    public getFdvMatrix(): Map<string, Map<string, AkmFDVCell>> {
        return this.fdvCellMap;
    }

    public clearFDV(): void {
        this.fdCellMap = new Map();
        this.fdvCellMap = new Map();
    }

    // rinçage complet sur modif mesures / changement vision
    public clear(): void {
        this.matrix.clear();
        this.neoCollectionMap = null;
        this.sistersMap = null;

        this.packCellMap = new Map();
        // this.collectionCells = new Map();
        this.comparePackMap = new Map();
        this.pendingCellMap = new Map();
    }

    // TODO TODO unifier les collections, ce sont des simples reverses....

    // map des dépendances de sisters
    // map sous la forme: level = scenar = measure(code) = AkmCell (une cellule blKey = *, les childrens sont toutes les baselines du step)
    private packCellMap: Map<number, Map<string, Map<string, Set<IBaselineValue>>>> = new Map();

    // map sous la forme: level = scenar = Map(mesureSource) = [packKey, Set<AkmCell>];
    // map des dépendances de pack comparePack
    private comparePackMap: Map<number, Map<string, Map<string, AkmPack>>> = new Map();


    /* ################################################
              gestion des FDVs
       ################################################ */

    /**
     * accesseur sur la map des dépendances sur les FDs
     * @param fdKey code FD
     * @returns la map par level, scen = set(cells)
     */
    public getFDCells(fdKey: string): AkmFDPairs {
        if (!this.fdCellMap.has(fdKey)) {
            this.fdCellMap.set(fdKey, { key: fdKey, pairMap: new Map(), cells: new Map() });
        }
        return this.fdCellMap.get(fdKey);
    }

    /**
     * accesseur sur la map des FDVs
     * @param fdKey code FD
     * @param fdvKey code FDV
     * @param createIfMissing forcer la création pour marquer les dépendances 
     * @returns 
     */
    public getFDVCell(fdKey: string, fdvKey: string, createIfMissing: boolean = false): AkmFDVCell {

        // TODO TODO suppression des informations de level ici sur la fdkey
        // if(fdKey === "SUPPLIER-POC#1") {
        //     console.log('getFDVCell', fdKey);
        // }
        // if(fdKey === "TPROD") {
        //     console.log('getFDVCell TPROD', fdKey);
        // }
        // LMA UF uniformisation de la matrice fdvCell, pas le level
        // fdKey = fdKey.split(WizConstants.CONTEXT_SEPARATOR)[0];
        
        let fdv: AkmFDVCell = this.fdvCellMap.get(fdKey)?.get(fdvKey) || null;
        if (!fdv && createIfMissing) { // création
            fdv = { fdKey, fdvKey };
            if (!this.fdvCellMap.has(fdKey)) { // La map n'existe pas encore. On la crée et on l'ajoute 
                this.fdvCellMap.set(fdKey, new Map());
            }
            this.fdvCellMap.get(fdKey).set(fdvKey, fdv);
        }
        return fdv;
    }

    /**
     * sur changement de locale, on fait perdre les labels sur les AkmFDVCell
     * TODO TODO gérer les descriptions & autres champs traductibles
     * permet la synchro
     */
    public forgetFDVLabels(): void {
        [...this.fdvCellMap.values()].forEach((maps: Map<string, AkmFDVCell>) => {
            [...maps.values()].forEach(cell => {
                delete cell.label;
                // delete cell.description;
                // cell = { rawVal: cell.rawVal, fdKey: cell.fdKey, fdvKey: cell.fdvKey, ch: cell.ch };
                // maps.set(cell.fdvKey, cell); // on remplace
            });
        });
    }

    /**
     * fait perdre la mémoire à une AkmFDVCell, sauf pour ses dépendances
     * permet la synchro sur retour d'update de FreeDataValue
     * @param fdKey 
     * @param fdvKey 
     */
    public forgetFDVCell(fdKey: string, fdvKey: string): AkmFDVCell {
        
        // LMA UF uniformisation de la matrice fdvCell, pas le level
        // fait sur l'appel depuis dataEngine
        // fdKey = fdKey.split(WizConstants.CONTEXT_SEPARATOR)[0];

        let cell: AkmFDVCell = this.fdvCellMap.get(fdKey)?.get(fdvKey) || null;
        if (cell) {
            let newCell: AkmFDVCell = { rawVal: cell.rawVal, fdKey: cell.fdKey, fdvKey: cell.fdvKey, ch: cell.ch };
            this.fdvCellMap.get(fdKey).set(fdvKey, newCell);
        }
        return this.getFDVCell(fdKey, fdvKey);
    }

    /**
     * suppression d'une entrée de Map des FDVs sur suppression du FDV
     * @param fdKey code FD
     * @param fdvKey code FDV
     */
    public removeFDVCell(fdKey: string, fdvKey: string): void {
        // LMA UF uniformisation de la matrice fdvCell, pas le level
        // fait sur l'appel depuis dataEngine
        // fdKey = fdKey.split(WizConstants.CONTEXT_SEPARATOR)[0];
        this.fdvCellMap.get(fdKey)?.delete(fdvKey);
    }

    /**
     * Ajout d'une dépendance sur un FD pour une clé de baseline (pour synchro sur modif FD)
     * @param level 
     * @param scenario 
     * @param baselineKey de la BL
     * @param measureKey mesure cible sur la BL
     * @param fdKey code du FD

     */
    public addFDCellChildren(level: number, scenario: string, baselineKey: string, measureKey: string, fdKey: string): AkmFDPairs {

        // recherche de l'existance de la dépendance (la cellule BLV), création si nécessaire
        let cell: IBaselineValue = this.addCell(level, baselineKey, scenario, measureKey, null, true);

        if (!this.fdCellMap.has(fdKey)) {
            this.fdCellMap.set(fdKey, { key: fdKey, pairMap: new Map(), cells: new Map() });
        }
        let fdMap = this.fdCellMap.get(fdKey).cells;

        if (!fdMap.get(level)?.get(scenario)?.has(cell)) {
            if (!fdMap.has(level)) { // La map par level n'existe pas encore. On la crée
                fdMap.set(level, new Map());
            }
            if (!fdMap.get(level).has(scenario)) { // La map par scenario n'existe pas encore. On la crée
                fdMap.get(level).set(scenario, new Set());
            }
            fdMap.get(level).get(scenario).add(cell); // ajout de la dépendance 
        }
        return this.fdCellMap.get(fdKey);
    }


    /**
     * Ajout d'une dépendance sur un FDV pour une clé de baseline (pour synchro sur modif FDV)
     * @param level 
     * @param scenario 
     * @param baselineKey de la BL
     * @param measureKey mesure cible sur la BL
     * @param fdKey code du FD
     * @param fdvKey code du FDV
     */
    public addFDVCellChildren(level: number, scenario: string, baselineKey: string, measureKey: string, fdKey: string, fdvKey: string): AkmFDVCell {

        const fdvCell: AkmFDVCell = this.getFDVCell(fdKey, fdvKey, true); // collecte ou creation de l'entrée de FDV

        // recherche de l'existance de la dépendance (la cellule BLV), création si nécessaire
        let cell: IBaselineValue = this.addCell(level, baselineKey, scenario, measureKey, null, true);

        if (!fdvCell.ch?.get(level)?.get(scenario)?.has(cell)) {
            if (!fdvCell.ch) { // La map n'existe pas encore. On la crée
                fdvCell.ch = new Map();
            }
            if (!fdvCell.ch.has(level)) { // La map par level n'existe pas encore. On la crée
                fdvCell.ch.set(level, new Map());
            }
            if (!fdvCell.ch.get(level).has(scenario)) { // La map par scenario n'existe pas encore. On la crée
                fdvCell.ch.get(level).set(scenario, new Set());
            }
            fdvCell.ch.get(level).get(scenario).add(cell); // ajout de la dépendance 
        }
        return fdvCell;
    }


    /* ################################################
              gestion pendings, calculs en attente
       ################################################ */

    // rinçage de la map des dépendance sur fin de calcul (ou avant de le préparer sur retour de commit)
    public clearPending(): void {
        this.pendingCellMap = new Map();
    }

    /**
     * Cherche si une cellule est dans la map en attente de calcul
     * sur INHERIT & ANCESTOR (prise du supérieur)
     * Pour ajout massif
     * @param requiredFei level#scen nécessaire pour jouer la cellule
     * @param targetFei level#scen cible du recalcul
     * @param blKey mesure cible
     * @param measureKey mesure de ma cellule
     * @returns la cellule
     */
    public pendingExist(requiredLevel: number, scenario: string, targetLevel: number, blKey: string, measureKey: string): boolean {
        let existCell: WidgetCell = this.pendingCellMap?.get(requiredLevel)?.get(scenario)?.get(targetLevel)?.get(blKey)?.get(measureKey);
        if (!!existCell) {
            return true;
        }
        return false;
    }

    /**
     * construit l'arbre de map de pending, si nécessaire
     * @param requiredFei fei nécessaire pour le calcul
     * @param targetFei fei à calculer
     */
    public checkPendingMapExist(requiredLevel: number, targetLevel: number, scenario: string): Map<string, Map<string, WidgetCell>> {
        if (!this.pendingCellMap.has(requiredLevel)) {
            this.pendingCellMap.set(requiredLevel, new Map());
        }
        if (!this.pendingCellMap.get(requiredLevel).has(scenario)) {
            this.pendingCellMap.get(requiredLevel).set(scenario, new Map());
        }
        if (!this.pendingCellMap.get(requiredLevel).get(scenario).has(targetLevel)) {
            this.pendingCellMap.get(requiredLevel).get(scenario).set(targetLevel, new Map());
        }

        return this.pendingCellMap.get(requiredLevel).get(scenario).get(targetLevel);
    }

    /** 
     * retourne les dépendances par level ET par scenar pour calculer le FEI ciblé par level
     * appelé par le dataLooper
     * @param level nécessaire pour jouer la cellule
     * @param scenario cible
     * @returns Map par step cible => blKey => measure
     */
    public getFeiPendingMap(level: number, scenario: string): Map<number, Map<string, Map<string, WidgetCell>>> {
        return this.pendingCellMap.get(level)?.get(scenario);
    }

    /**
     * inscription d'une dépendance dans la map sur retour de commit, pour recalcul ciblé
     * appelé par le dataEngine
     * on est ici sur des dépendances du même step
     * @param requiredLevel nécessaire pour jouer la cellule
     * @param scenario cible
     * @param cells cellules à recalculer sur mise à dispo de ce niveau
     */
    public massAddPendingCell(level: number, scenario: string, cells: Set<WidgetCell>): void {
        // la map n'est pas forcément existante
        this.checkPendingMapExist(level, level, scenario);

        [...cells.values()].forEach((cell: WidgetCell) => {
            if (!!cell) {
                if (!this.pendingCellMap.get(level).get(scenario).get(level).has(cell.k)) {
                    this.pendingCellMap.get(level).get(scenario).get(level).set(cell.k, new Map());
                }
                this.pendingCellMap.get(level).get(scenario).get(level).get(cell.k).set(cell.m, cell);
            } else {
                console.log('empty cell on register pending', level, scenario, cells);
            }

        });
    }

    /**
     * Ajout d'une cellule à la map des calculs en attente
     *
     * @param requiredLevel nécessaire pour jouer la cellule
     * @param cell: WidgetCell
     * @returns la WidgetCell
     */
    public addPendingCell(requiredLevel: number, cell: WidgetCell): WidgetCell {
        // la map n'est pas forcément existante
        let pendings: Map<string, Map<string, WidgetCell>> = this.checkPendingMapExist(requiredLevel, cell.l, cell.sc);

        // ajout dans la map des pendings
        if (!pendings.has(cell.k)) {
            pendings.set(cell.k, new Map());
        }
        pendings.get(cell.k).set(cell.m, cell);
        return cell;
    }

    /* ################################################
              gestion des dépendances intercellules
       ################################################ */

    /**
     * construction à la volée si nécessaire de la WidgetCell sur la WidgetLine
     * pour les mesures inter-steps
     * @param line WidgetLine
     * @param measure recherché
     * @returns la widgetCell, garantie
     */
    public getWidgetCrossStepCell(line: WidgetLine, measure: string): WidgetCell {
        if (!line.cells.has(measure)) {
            line.cells.set(measure, { l: line.level, k: line.key, sc: line.scen, m: measure });
        }



        return line.cells.get(measure);
    }


    /**
     * construction à la volée si nécessaire de la WidgetCell sur la WidgetLine
     * @param line WidgetLine
     * @param measure recherché
     * @returns la widgetCell, garantie
     */
    public getWidgetCell(line: WidgetLine, measure: string): WidgetCell {
        if (!line.cells) {
            line.cells = new Map();
        }
        if (!line.cells.has(measure)) {
            line.cells.set(measure, { l: line.level, k: line.key, sc: line.bl.scen, m: measure });
        }
        return line.cells.get(measure);
    }

    /**
     * Ajout d'une dépendance à une cellule
     * la IBaselineValue motherValue est nécessaire pour calculer childCell
     * on va donc ajouter childCell dans les enfants de motherValue
     * 
     * @param motherValue la IBaselineValue sur laquelle ou ajoute un enfant
     * @param childCell la cellule à recalculer quand motherValue change
     */
    public addDependancy(motherValue: IBaselineValue, childCell: WidgetCell) {
        if (!motherValue.w) {  // Le set n'existe pas encore. On le crée
            motherValue.w = new Set();
        }

        // LMA MEGACELLS,
        // on travaille par INSTANCE
        let realCellInstance: WidgetCell = this.matrix.getCellInstance(childCell);
        motherValue.w.add(realCellInstance);
;       
    }

    /**
     * collecte la valeur d'une IbaselineValue mère, et ajoute la childCell en dépendance
     * @param stepLevel de la widgetLine cible
     * @param blKey de la widgetLine cible
     * @param scenario de la widgetLine cible
     * @param mKey code recherché sur la widgetLine cible
     * @param childCell cellule dépendante de cette valeur
     * @returns la valeur
     */
    public getCellValueAndAddDependency(stepLevel: number, blKey: string, scenario: string, mKey: string, childCell: WidgetCell): string {
        let result: string = null;
        let motherValue: IBaselineValue = this.getCell(stepLevel, blKey, scenario, mKey);

        if (!childCell) {
            console.log('getCellValueAndAddDependency no child ??');
        }

        if (!!motherValue) {
            this.addDependancy(motherValue, childCell);
            result = motherValue.v || null;
        } else {
            // console.log('getCellValueAndAddDependency no mother', blKey);
            motherValue = this.addCell(stepLevel, blKey, scenario, mKey, null, true);
            this.addDependancy(motherValue, childCell);
        }
        return result;
    }

    /**
     * Création d'une IBaselineValue "mère" avec ajout d'une dépendance WidgetCell
     * pour recalcul de la fille sur maj de la mère
     * 
     * @param requiredLevel step de la mère
     * @param requiredScenario scenario de la mère
     * @param requiredBlKey clé de la mère
     * @param requiredMeasureKey mesure sur la mère, qui déclenche le recalcul
     * @param widgetCell la cellule dépendante
     * @returns la IBaselineValue mère
     */
    public addCellWithDependency(requiredLevel: number, requiredScenario: string, requiredBlKey: string, requiredMeasureKey: string, widgetCell: WidgetCell): IBaselineValue {
        const motherCell: IBaselineValue = this.addCell(requiredLevel, requiredBlKey, requiredScenario, requiredMeasureKey, null, true); // on ajoute la cellule mère
        this.addDependancy(motherCell, widgetCell); // on pousse la fille en dépendance sur la mère
        return motherCell;
    }

    /* ################################################
              gestion de la matrice
       ################################################ */

    /**
     * Suppression d'une baseline
     * 
     * @param stepLevel du step
     * @param blKey de la baseline
     * @param scenario cible
     */
    public deleteRow(stepLevel: number, blKey: string, scenario: string) {
        this.matrix.deleteRow(stepLevel, blKey, scenario);
        this.deleteCollectionByTargetLevelAndScenario(stepLevel, scenario);
    }

    /**
     * Ajout d'une cellule à la map, ou mise à jour
     * @param stepLevel step cible
     * @param blKey clé de baseline
     * @param scenario cible
     * @param measureKey clé de mesure ou de trio
     * @param valor string
     * @param createOnly : simple création, pas de modif des valeurs si existe (lien sur FDV)
     * @returns la cellule
     */
    public addCell(stepLevel: number, blKey: string, scenario: string, measureKey: string, valor: string, createOnly: boolean = false): IBaselineValue {

        const row: IBaseline = this.matrix.getRow(stepLevel, blKey, scenario, true); // prise de la ligne, et création si nécessaire
        let cell: IBaselineValue = row.vs[measureKey] || null;

        if (!cell) { // création
            cell = {
                l: stepLevel,
                sc: scenario,
                k: blKey,
                m: measureKey,
                v: valor
            }

            row.vs[measureKey] = cell;
        } else {
            if (!createOnly) {
                cell.v = valor;
            }
        }

        return cell;
    }

    /**
     * alimentation des clés de filter / et mesures pour une baseline (row)
     * @param stepLevel du step
     * @param blKey de la baseline
     * @param scenario cible, FAKE... pour Fake
     * @param keyMap les couples clé / valeur
     * @returns la ligne (une baseline)
     */
    public addCells(stepLevel: number, blKey: string, scenario: string, keyMap: { [key: string]: string }): void {
        const row: IBaseline = this.matrix.getRow(stepLevel, blKey, scenario, true);

        let cell: IBaselineValue;
        Object.keys(keyMap).forEach((mkey: string) => {
            cell = row.vs[mkey] || null;

            // if(mkey === 'LMA_ED1#I' && blKey=== "PLAN2SCENAR|PLAN2SCENAR_102|KLE" ) {
            //     console.log('maxtrix addCells', keyMap[mkey]);
            // }

            if (!cell) {
                row.vs[mkey] = { l: stepLevel, sc: scenario, k: blKey, m: mkey, v: keyMap[mkey] };
            } else { // existe, simple mise à jour de la valeur
                cell.v = keyMap[mkey];
            }
        });
    }

    /**
     * accesseur sur une cellule
     * 
     * @param stepLevel du step
     * @param blKey de la baseline
     * @param scenario cible, FAKE... pour Fake
     * @param mKey measureCode
     * @returns
     */
    public getCell(stepLevel: number, blKey: string, scenario: string, mKey: string): IBaselineValue {
        return this.matrix.getRow(stepLevel, blKey, scenario)?.vs[mKey] || null;
    }

    /**
     * collecte d'une valeur de cellule
     * @param stepLevel du step
     * @param blKey de la baseline
     * @param scenario cible, FAKE... pour Fake
     * @param mKey code recherché pour la valeur
     * @returns la valeur au format string
     */
    public getCellValue(stepLevel: number, blKey: string, scenario: string, mKey: string): string {
        return this.getCell(stepLevel, blKey, scenario, mKey)?.v || null;
    }

    /**
     * collecte d'une valeur de cellule, avec ajout de la référence en dépendance
     * utilisé dans le formulaCoreEngine
     * @param requiredWidgetCell la cellule dont j'ai besoin
     * @param targetWidgetCell moi
     * @param fakeMode, pas d'inscription des fakes en matrice
     * @returns le string qui correspond à la valeur de la cellule requise
     */
    public getDependancyCellValue(requiredWidgetCell: WidgetCell, targetWidgetCell: WidgetCell, fakeMode: boolean = false): string {
        const requiredCell: IBaselineValue = this.getCell(requiredWidgetCell.l, requiredWidgetCell.k, requiredWidgetCell.sc, requiredWidgetCell.m);
        if (!!requiredCell && !fakeMode) {
            const targetCell: IBaselineValue = this.addCell(targetWidgetCell.l, targetWidgetCell.k, targetWidgetCell.sc, targetWidgetCell.m, null, true); // on ajoute la cellule à calculer
            this.addDependancy(requiredCell, targetWidgetCell);
        }
        return requiredCell?.v || null;
    }

    /* ################################################
            gestion des collections
    ################################################ */

    /**
     * la Map des collections
     * - level cible de la collecte
     *   - scenario cible
     *       - level du calcul => permet de prendre la splittedKey sur la BL
     *           - mesure demandée sur la cible
     *               - la clé de la cellule parente
     *                   - le Set de WidgetCell (en cas de calcul multiples sur une même mesure de la sous-collection)
     */
    private neoCollectionMap: Map<number, Map<string, Map<number, Map<string, Map<string, Set<WidgetCell>>>>>> = new Map();

    /**
     * accesseur sur les collections pour un FEI (computeEngine)
     * @param level 
     * @param scenario 
     */
    public getFeiCollectionMap(level: number, scenario: string): Map<number, Map<string, Map<string, Set<WidgetCell>>>> {
        let result: Map<number, Map<string, Map<string, Set<WidgetCell>>>> = this.neoCollectionMap?.get(level)?.get(scenario) || null;
        return result;
    }

    /**
     * injection en masse des collections pour un FEI (computeEngine)
     * @param level 
     * @param scenario 
     * @param la collection de dépendances
     */
    public injectFeiCollectionMap(level: number, scenario: string, collections: Map<number, Map<string, Map<string, Set<WidgetCell>>>>): void {

        this.neoCollectionMap.set(level, new Map());
        this.neoCollectionMap.get(level).set(scenario, collections);
    }

    /**
     * contrôle si une cellule de type collection a déjà été générée (pour cette collection)
     * @param cell 
     * @param level 
     * @param scenario
     * @param measure 
     * @returns 
     */
    public neoCollectionExist(cell: WidgetCell, level: number, scenario: string, measure: string): boolean {
        return this.neoCollectionMap?.get(level)?.get(scenario)?.get(cell.l)?.get(measure)?.get(cell.k)?.has(cell);
    }

    /**
     * sur suppression d'une ligne, suppression des collections persistées liées
     * @param targetLevel 
     * @param targetScenario
     */
    public deleteCollectionByTargetLevelAndScenario(targetLevel: number, targetScenario: string): void {
        // console.log('deleteCollectionByTargetLevelAndScenario', this.neoCollectionMap);
        // on est au niveau scenar Map<string, Map<number, Map<string, Map<string, Set<WidgetCell>>>>>
        if (this.neoCollectionMap?.size > 0) {
            [...this.neoCollectionMap.values()].forEach(map => {
                if (map.has(targetScenario)) { // on cherche le scenar
                    let result = map.get(targetScenario).delete(targetLevel);
                    // console.log('deleteCollectionByTargetLevelAndScenario', result, this.neoCollectionMap);
                }
            });
        }

    }

    /**
     * création d'une entrée de collection
     * @param cell la cellule de type collection
     * @param childrenLevel le niveau cible
     * @param scenario
     * @param children les cellules sources
     * @param measureKey la mesure cible sur les enfants
     */
    public setNeoCollectionDependencies(cell: WidgetCell, childrenLevel: number, scenario: string, children: WidgetLine[], measureKey: string): void {
        if (!this.neoCollectionMap) {
            this.neoCollectionMap = new Map();
        }
        if (!this.neoCollectionMap.has(childrenLevel)) {
            this.neoCollectionMap.set(childrenLevel, new Map());
        }
        if (!this.neoCollectionMap.get(childrenLevel).has(scenario)) {
            this.neoCollectionMap.get(childrenLevel).set(scenario, new Map());
        }
        if (!this.neoCollectionMap.get(childrenLevel).get(scenario).has(cell.l)) {
            this.neoCollectionMap.get(childrenLevel).get(scenario).set(cell.l, new Map());
        }
        if (!this.neoCollectionMap.get(childrenLevel).get(scenario).get(cell.l).has(measureKey)) {
            this.neoCollectionMap.get(childrenLevel).get(scenario).get(cell.l).set(measureKey, new Map());
        }
        if (!this.neoCollectionMap.get(childrenLevel).get(scenario).get(cell.l).get(measureKey).has(cell.k)) {
            this.neoCollectionMap.get(childrenLevel).get(scenario).get(cell.l).get(measureKey).set(cell.k, new Set<WidgetCell>());
        }
        this.neoCollectionMap.get(childrenLevel).get(scenario).get(cell.l).get(measureKey).get(cell.k).add(cell);

        children.forEach((wl: WidgetLine) => { // ajout des dépendances sur les enfants de la collection
            // les IbaselineValue des enfants
            // TODO poser les écouteurs sur les bons scenar, prise de la bl 
            let childValue: IBaselineValue = this.addCell(wl.level, wl.key, scenario, measureKey, null, true);
            this.addDependancy(childValue, cell);
        });
    }

    /**
     * en cas d'ajout / suppression de WidgetLine, collecte des cellules parentes à rejouer sur les collections
     * @param fei 
     * @param wl 
     * @param creation d'une nouvelle widgetLine, il faut ajouter les dépendances
     * @returns Set<WidgetCell>
     */
    public getNeoCollectionDependencies(fei: FeiVO, wl: WidgetLine, creation: boolean = false): Set<WidgetCell> {
        let result: Set<WidgetCell> = new Set();
        let collMap = this.neoCollectionMap;
        if (collMap?.get(wl.level)?.has(wl.scen)) {
            // on prépare la clé splittée si  nécessaire
            if (!wl.bl.splittedKey) {
                wl.bl.splittedKey = wl.key.split(WizConstants.KEY_SEPARATOR);
            }

            [...collMap.get(wl.level).get(wl.scen).keys()].forEach((masterLevel: number) => { // on collecte les niveaux demandeurs
                let myKeyForLevel: string = wl.bl.splittedKey.slice(0, fei.vision.keySizeByLevel.get(masterLevel)).join(WizConstants.KEY_SEPARATOR);

                [...collMap.get(wl.level).get(wl.scen).get(masterLevel).keys()].forEach((measureCode: string) => {
                    [...collMap.get(wl.level).get(wl.scen).get(masterLevel).get(measureCode).keys()].forEach((motherKey: string) => {
                        if (motherKey === myKeyForLevel) { // c'est ma ligne mère
                            [...collMap.get(wl.level).get(wl.scen).get(masterLevel).get(measureCode).get(motherKey).values()].forEach((cell: WidgetCell) => {
                                // je l'ajoute à la pile d'update
                                result.add(cell);
                                // et je l'inscris dans mes dépendances si je suis en mode création
                                if (creation) {
                                    let childValue: IBaselineValue = this.addCell(wl.level, wl.key, wl.scen, measureCode, null, true);
                                    this.addDependancy(childValue, cell);
                                }
                            });
                        }
                    });
                });
            });
        }

        return result;
    }

    /* ################################################
      gestion des dépendances de sisters
       ################################################ */

    // map des dépendances de sisters
    // map sous la forme: level = scenar = 
    //                  {pkey ou moyen de la calculer ? }
    //                           = sisterResult {motherKey pour split, resultat du calcul, nbr de soeurs

    // moyen de la calculer: prendre un clone du mmi de la mesure, générer la clé et comparer avec le résulat
    // donc objet sisterKey {pkey, mmi}, si mmi on l'utilise pour générer une clé
    //                      level       scenar    mesurecible#aggregat      clé sisters   résultat
    private sistersMap: Map<number, Map<string, Map<string, Map<string, SisterResult>>>> = null;

    // TODO TODO sur create / delete, idenfier les séries auxquelles m'ajouter

    /**
     * création d'une entrée de sisters
     * @param level le niveau cible
     * @param scenario 
     * @param measureFullKey mesure|aggregat
     * @param sisterMap la map de sisterResult, déjà construite, par clé
     */
    public setNeoSistersDependencies(level: number, scenario: string, measureFullKey: string,
        sisterMap: Map<string, SisterResult>): void {

        if (!this.sistersMap) {
            this.sistersMap = new Map();
        }
        if (!this.sistersMap.has(level)) {
            this.sistersMap.set(level, new Map());
        }
        if (!this.sistersMap.get(level).has(scenario)) {
            this.sistersMap.get(level).set(scenario, new Map());
        }
        if (!this.sistersMap.get(level).get(scenario).has(measureFullKey)) {
            this.sistersMap.get(level).get(scenario).set(measureFullKey, new Map());
        }

        // let measureKey: string = measureFullKey.split(WizConstants.KEY_SEPARATOR)[0];

        [...sisterMap.keys()].forEach((sisterKey: string) => {
            let sisterResult: SisterResult = sisterMap.get(sisterKey);
            this.sistersMap.get(level).get(scenario).get(measureFullKey).set(sisterKey, sisterResult);
            let cell: IBaselineValue;

            if (!!sisterResult.rMap) { // fonctionne pour le slide
                [...sisterResult.rMap.keys()].forEach((widgetLine: WidgetLine) => {
                    let scen: string = widgetLine.cells?.get(sisterResult.mk)?.sc || scenario;
                    cell = this.addCell(level, widgetLine.key, scen, sisterResult.mk, null, true);
                    this.addSisterReference(cell, sisterResult); // va permettre de jouer les refresh
                    
                    sisterResult.ok?.forEach(s => { // autres dépendances de mesures
                        cell = this.addCell(level, widgetLine.key, scen, s, null, true);
                        this.addSisterReference(cell, sisterResult); // va permettre de jouer les refresh
                    });

                });
            } else { // TODO TODO toujours alimenter le resultMap
                sisterResult.sis.forEach((widgetCell: WidgetCell) => {
                    cell = this.addCell(level, widgetCell.k, widgetCell.sc, sisterResult.mk, null, true);
                    this.addSisterReference(cell, sisterResult); // va permettre de jouer les refresh
                });
            }


        });
    }

    /**
     * contrôle que les sisters sont calculées pour une mesure donnée (et son aggrégat)
     * 
     * @param level 
     * @param scenario 
     * @param measureFullKey meas|agg
     * @param sisterKey: string
     * @returns true si une entrée
     */
    public exitSisterResult(level: number, scenario: string, measureFullKey: string, sisterKey: string): boolean {
        return this.sistersMap?.get(level)?.get(scenario)?.get(measureFullKey)?.has(sisterKey) || false;
    }


    /**
     * sur création ou suppression de baseline, reset des Series à rejour
     * @param wl 
     * @return les cellules à rejouer, pour ajout aux pendings
     */
    public killSerieOnCreateDelete(wl: WidgetLine): Set<WidgetCell> {
        let result: Set<WidgetCell> = new Set();
        let myMap: Map<string, Map<string, SisterResult>> = this.sistersMap?.get(wl.level)?.get(wl.scen) || null;
        if (!myMap) {
            return result;
        }

        // TODO TODO Faire un wl.serieKeyMap?.has(measureFullKey),  la boucle sur les sisKey est inutile
        // console.log('killSerieOnCreateDelete', this.sistersMap, wl);
        // les mesures, on s'en fout 
        [...myMap.keys()].forEach((measureFullKey: string) => {
            // par contre, pas les sisterKey. Soit pKey soit *
            [...myMap.get(measureFullKey).keys()].forEach((sisKey: string) => {
                let isOK: boolean = false;

                let sistKeyParts: string[] = sisKey.split(WizConstants.KEY_SEPARATOR);

                if (sisKey === WizConstants.wildcardDomain) { // on est sur du FOLKS, j'suis forcément concerné
                    isOK = true;
                } else if (sisKey === wl.bl.pkey) { // ma pkey, c'est OK aussi
                    isOK = true;
                } else {
                    let allWilds: boolean = true;
                    sistKeyParts.forEach(s => {
                        if (s !== WizConstants.wildcardDomain) {
                            allWilds = false;
                        }
                    });
                    isOK = allWilds;
                }

                if (isOK) {
                    myMap.get(measureFullKey).get(sisKey).sis.forEach(cell => {
                        result.add(cell);
                    });
                    myMap.get(measureFullKey).delete(sisKey); // et je la tue
                }

                // gestion des clés complexes pour les slide, From, Twins...
                if (wl.serieKeyMap?.has(measureFullKey)) {
                    let sisResult: SisterResult = myMap.get(measureFullKey).get(wl.serieKeyMap?.get(measureFullKey));
                    if (sisResult) {
                        sisResult.sis.forEach(cell => {
                            result.add(cell);
                        });
                    }
                    myMap.get(measureFullKey).delete(wl.serieKeyMap.get(measureFullKey));
                }

            });
        });

        return result;
    }



    /**
     * suppression d'une entrée de série, pour forcer sa reconstruction
     * @param level 
     * @param scenario 
     * @param measureFullKey meas|agg
     * @param sisterKey sous forme de parentKey
     * @returns rien
     */
    public killSerie(level: number, scenario: string, measureFullKey: string, sisterKey: string): void {
        this.sistersMap?.get(level)?.get(scenario)?.get(measureFullKey)?.delete(sisterKey);
    }

    /**
     * collecte d'un sisterResult
     * @param level 
     * @param scenario 
     * @param measureFullKey meas|agg
     * @param sisterKey sous forme de parentKey
     * @returns SisterResult
     */
    public getSisterResultByPkey(level: number, scenario: string, measureFullKey: string, sisterKey: string): SisterResult {
        return this.sistersMap.get(level).get(scenario).get(measureFullKey).get(sisterKey) || null;
    }

    /**
     * Ajout d'une dépendance de série
     * la IBaselineValue sister est un élément de la série
     * OU la mother en cas de SPLIT
     * 
     * @param sister la IBaselineValue sur laquelle ou ajoute une dépendance de série
     * @param sisterResult le pool de sisters
     */
    public addSisterReference(sister: IBaselineValue, sisterResult: SisterResult): void {
        if (!sister.si) {  // Le set n'existe pas encore. On le crée
            sister.si = new Set();
        }

        // LMA MEGACELLS,
        // on travaille par INSTANCE
        let realSisterInstance: SisterResult = this.matrix.getSisterResultInstance(sisterResult);
        sister.si.add(realSisterInstance);
    }


    /**
     * retourne toutes les sisters pour un fei
     * 
     * @param level 
     * @param scenario 
     * @returns 
     */
    public getAllSisters(level: number, scenario: string): Set<IBaselineValue> {
        let allsisters: Set<IBaselineValue> = new Set();
        if (!!this.packCellMap.get(level)?.has(scenario)) {
            [...this.packCellMap.get(level).get(scenario).values()].forEach(sisterSet => {
                allsisters = new Set([...allsisters, ...sisterSet]);
            })
        }
        return allsisters;
    }

    /**
     * retourne un pack de sisters pour une mesure donnée
     * 
     * @param level 
     * @param scenario 
     * @param measureKey 
     * @returns Set<AkmCell>
     */
    public getSisters(level: number, scenario: string, measureKey: string): Set<IBaselineValue> {
        return this.packCellMap.get(level)?.get(scenario)?.get(measureKey) || null;
    }

    /**
     * ajout d'une collection de ligne en dépendance
     * @param level 
     * @param scenario 
     * @param sourceMKey mesure source qui impacte le calcul des sisters
     * @param mKey mesure à recalculer sur les sisters
     * @param sisters WidgetLine[] cibles à recalculer
     * @returns
     */
    public setSistersDependencies(level: number, scenario: string, sourceMKey: string, mKey: string, sisters: WidgetLine[]): void {
        // ajout au pack de dépendances
        let pack: Set<IBaselineValue> = this.checkPackMapExist(level, scenario, sourceMKey);

        let cell: IBaselineValue;
        sisters.forEach((wl: WidgetLine) => {
            cell = this.addCell(level, wl.key, scenario, mKey, null, true);
            pack.add(cell);
        });
    }

    /* ################################################
     *   Gestion du compare pack
     * ################################################ */

    // TODO cibler d'avantage
    /**
     * retourne les pack pour le couple fei / mesure
     * @param level 
     * @param scenario 
     * @param mesure cible
     * @returns [clé unique du pack sur le fei, set<cells>]
     */
    public getComparePack(level: number, scenario: string, mKey: string): AkmPack {
        // console.log('getComparePack', uniqueKey, mKey, this.comparePackMap);
        return this.checkComparePackMapExist(level, scenario, mKey);
    }

    /**
     * ajout d'une collection de ligne en dépendance (compareRange, FalseTwins..)
     * @param level 
     * @param scenario 
     * @param packKey code unique du pack
     * @param mKey mesure à recalculer sur les sisters
     * @param lines WidgetLine[] cibles à recalculer (toutes celles du fei pour le scenar)
     * @returns
     */
    public setPackDependencies(level: number, scenario: string, packKey: string, mKey: string, lines: WidgetLine[]): void {

        let pack: AkmPack = this.checkComparePackMapExist(level, scenario, mKey);
        if (!pack) { // existe pas encore
            pack = { key: packKey, cells: new Set() };
            this.comparePackMap.get(level).get(scenario).set(mKey, pack);
        }

        // const pack: Set<AkmCell> = this.comparePackMap.get(uniqueName).get(mKey)[1];
        let cell: IBaselineValue;
        lines.forEach((wl: WidgetLine) => {
            // const childKey: string = [uniqueName, bl.key, mKey].join(WizConstants.WIDGET_KEY_SEPARATOR); // la cellule cible à recalculer
            // TODO ici ???
            cell = this.addCell(level, wl.key, scenario, mKey, null, true);
            pack.cells.add(cell);
        });
    }

    /**
     * construit l'arbre de map de collection, si nécessaire et retourne le set de lignes de la collection
     * 
     * @param level 
     * @param scenario 
     * @returns Set<lvl, key, mkey>
     */
    // private checkCollectionMapExist(level: number, scenario: string): Set<string> {
    //     if (!this.collectionCellMap.has(level)) { 
    //         this.collectionCellMap.set(level, new Map());
    //     }
    //     if (!this.collectionCellMap.get(level).has(scenario)) {
    //         this.collectionCellMap.get(level).set(scenario, new Set());
    //     }
    //     return this.collectionCellMap.get(level).get(scenario);
    // }


    /**
     * construit l'arbre de map des pack, si nécessaire et retourne le set de cellules 
     * 
     * @param level 
     * @param scenario 
     * @param measureCode
     * @returns Set<AkmCell>
     */
    private checkPackMapExist(level: number, scenario: string, measureCode: string): Set<IBaselineValue> {
        if (!this.packCellMap.has(level)) {
            this.packCellMap.set(level, new Map());
        }
        if (!this.packCellMap.get(level).has(scenario)) {
            this.packCellMap.get(level).set(scenario, new Map());
        }
        if (!this.packCellMap.get(level).get(scenario).has(measureCode)) {
            this.packCellMap.get(level).get(scenario).set(measureCode, new Set());
        }

        return this.packCellMap.get(level).get(scenario).get(measureCode);
    }

    /**
     * construit l'arbre de map des pack, si nécessaire et retourne le set de cellules 
     * 
     * @param level 
     * @param scenario 
     * @param measureCode
     * @returns Set<AkmCell>
     */
    private checkComparePackMapExist(level: number, scenario: string, measureCode: string): AkmPack {
        if (!this.comparePackMap.has(level)) {
            this.comparePackMap.set(level, new Map());
        }
        if (!this.comparePackMap.get(level).has(scenario)) {
            this.comparePackMap.get(level).set(scenario, new Map());
        }
        if (!this.comparePackMap.get(level).get(scenario).has(measureCode)) {
            this.comparePackMap.get(level).get(scenario).set(measureCode, null);
        }

        return this.comparePackMap.get(level).get(scenario).get(measureCode);
    }


}